import React, { useState } from 'react';
import { useClinicalDemo } from '../../contexts/ClinicalDemoContext';
import { documentationOnlyLabel, visits } from '../../data/clinicalDemo';
import type { VisitReviewOutcome } from '../../types/clinicalDemo';
import { DemoSectionHeader } from '../demoShell/DemoSectionHeader';
import { formatTimestamp } from '../../utils/demoStorage';
import { errorClass, fieldClass, focusRing, glassPanel, labelClass, primaryButton } from '../../utils/demoUi';
import { visitStatus, visitStatusLabels } from '../../utils/clinicalDemo';
import { CategoryTag } from './CategoryTag';
const outcomes: {
  id: VisitReviewOutcome;
  label: string;
}[] = [{
  id: 'clarification-requested',
  label: 'Clarification requested'
}, {
  id: 'reviewed',
  label: 'Reviewed'
}];
export function DocumentationReview() {
  const {
    state,
    dispatch
  } = useClinicalDemo();
  const visitId = state.ui.selectedVisitId;
  const entries = state.entries.filter(entry => entry.visitId === visitId);
  const reviews = state.reviews.filter(review => review.visitId === visitId);
  const canReview = state.ui.workspace === 'review';
  const draft = state.ui.reviewDrafts[visitId] ?? {
    outcome: '',
    explanation: ''
  };
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!draft.outcome) return setError('Choose a review outcome.');
    if (!draft.explanation.trim()) return setError('Write an explanation for this review.');
    setError('');
    dispatch({
      type: 'submitReview',
      visitId
    });
    setMessage(`Review recorded for ${visitId} with ${entries.length} entries included.`);
  };
  return <div data-id="element-134" data-mp="1nlz7f8jg9el2">
      <DemoSectionHeader title="Documentation Review" description={canReview ? 'Check that each entry is categorized and complete. Reviews record which entries they covered.' : 'Review outcomes recorded by authorized reviewers. Reviews are made in the Authorized Review Workspace.'} data-id="element-135" data-mp="1nm8zr2ryvxtx" />

      <div role="group" aria-label="Choose a visit" className="mb-6 flex flex-wrap gap-2" data-id="element-136" data-mp="372dds79771gp">
        {visits.map(visit => <button key={visit.id} type="button" aria-pressed={visit.id === visitId} onClick={() => {
        setMessage('');
        dispatch({
          type: 'selectVisit',
          visitId: visit.id
        });
      }} className={`rounded-xl border px-4 py-2 text-left text-sm transition-colors duration-150 ${focusRing} ${visit.id === visitId ? 'border-navy bg-white shadow-premium' : 'border-line bg-white/60 hover:bg-white'}`} data-id="element-137" data-mp="3b2dzmcymmexs">
            <span className="block font-semibold text-ink-dark" data-id="element-138" data-mp="2xapr0jnl68lk">{visit.label}</span>
            <span className="text-xs text-ink-muted" data-id="element-139" data-mp="323itt1zhv6v0">{visitStatusLabels[visitStatus(state, visit.id)]}</span>
          </button>)}
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2" data-id="element-140" data-mp="29mg8bqlo60yn">
        <section aria-labelledby="entries-under-review" className={`${glassPanel} p-6`} data-id="element-141" data-mp="29klpqixvekxq">
          <h2 id="entries-under-review" className="font-heading text-lg font-bold text-ink-dark" data-id="element-142" data-mp="222f5as34mely">
            Entries ({entries.length})
          </h2>
          <ul className="mt-3 divide-y divide-line" data-id="element-143" data-mp="1s4him90x0pvg">
            {entries.map(entry => <li key={entry.id} className="py-3" data-id="element-144" data-mp="1n5wsj2fdnmkq">
                <div className="flex flex-wrap items-center gap-2" data-id="element-145" data-mp="1n54ulkyb5cra">
                  <CategoryTag category={entry.category} data-id="element-146" data-mp="1j4b6orkv7xye" />
                  <span className="text-xs text-ink-muted" data-id="element-147" data-mp="1j5w4qr6e1z10">
                    {entry.id}
                    {entry.isAddendum ? ' · addendum' : ''}
                  </span>
                </div>
                <p className="mt-1.5 text-sm leading-relaxed text-ink-dark" data-id="element-148" data-mp="19bx3due52ja4">{entry.text}</p>
              </li>)}
          </ul>
        </section>

        <div className="space-y-6" data-id="element-149" data-mp="zf0vnqw2e16m">
          {canReview && <form onSubmit={submit} noValidate className={`${glassPanel} p-6`} data-id="element-150" data-mp="veqpad4d18r8">
              <p className="rounded-lg border-l-4 border-amber-300 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-900" data-id="element-151" data-mp="vgurmqmm1zpe">{documentationOnlyLabel}</p>
              <fieldset className="mt-4" data-id="element-152" data-mp="1p2wi2rxv2uah">
                <legend className={labelClass} data-id="element-153" data-mp="1p626e4yo1s55">Outcome</legend>
                <div className="mt-2 flex flex-wrap gap-2" data-id="element-154" data-mp="1bcb8jtuarwp9">
                  {outcomes.map(outcome => <label key={outcome.id} className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold ${draft.outcome === outcome.id ? 'border-lavender bg-lavender-pale' : 'border-line bg-white'}`} data-id="element-155" data-mp="1fcldr4a9mj32">
                      <input type="radio" name={`clinical-outcome-${visitId}`} checked={draft.outcome === outcome.id} onChange={() => dispatch({
                  type: 'setReviewDraft',
                  visitId,
                  draft: {
                    ...draft,
                    outcome: outcome.id
                  }
                })} className="h-4 w-4 accent-[#6366C9]" data-id="element-156" data-mp="wf0pnzfbroji" />
                      {outcome.label}
                    </label>)}
                </div>
              </fieldset>
              <label htmlFor={`clinical-explanation-${visitId}`} className={`mt-4 ${labelClass}`} data-id="element-157" data-mp="oxly3bmsn1ub">
                Explanation
              </label>
              <textarea id={`clinical-explanation-${visitId}`} rows={4} value={draft.explanation} onChange={event => dispatch({
            type: 'setReviewDraft',
            visitId,
            draft: {
              ...draft,
              explanation: event.target.value
            }
          })} className={fieldClass} data-id="element-158" data-mp="ytytcdxzlnr6" />
              {error && <p className={errorClass} data-id="element-159" data-mp="12wbv6r9m51io">{error}</p>}
              <p className="mt-3 text-xs text-ink-muted" data-id="element-160" data-mp="39m07zlibf9ub">
                The review will record all {entries.length} current entries. After “Reviewed”, entries are preserved and later changes are appended as addenda.
              </p>
              <div className="mt-4 flex flex-wrap items-center gap-3" data-id="element-161" data-mp="18lc51dpw6uw1">
                <button type="submit" className={primaryButton} data-id="element-162" data-mp="1dilsintq0wh8">
                  Save review
                </button>
                <p role="status" className="text-sm font-medium text-navy" data-id="element-163" data-mp="1ih5ir0fckufe">
                  {message}
                </p>
              </div>
            </form>}

          <section aria-labelledby="visit-reviews" className={`${glassPanel} p-6`} data-id="element-164" data-mp="1rjeolczo7zmg">
            <h2 id="visit-reviews" className="font-heading text-lg font-bold text-ink-dark" data-id="element-165" data-mp="1whznn936sd9b">
              Review history
            </h2>
            {reviews.length === 0 ? <p className="mt-2 text-sm text-ink-muted" data-id="element-166" data-mp="21assdwynnlle">This visit has not been reviewed.</p> : <ol className="mt-3 space-y-3" data-id="element-167" data-mp="2a9eajr07syln">
                {[...reviews].reverse().map(review => <li key={review.id} className="rounded-lg border border-line bg-white px-4 py-3 text-sm" data-id="element-168" data-mp="2f8h70n24pwi0">
                    <p className="font-semibold text-ink-dark" data-id="element-169" data-mp="3kvq693g2dfn">
                      {visitStatusLabels[review.outcome]} <span className="font-normal text-ink-muted" data-id="element-170" data-mp="3uqx8n2pqo9bb">· {review.id} · {review.reviewerRole} · {formatTimestamp(review.at)}</span>
                    </p>
                    <p className="mt-1 leading-relaxed text-ink-dark" data-id="element-171" data-mp="3qrxwax1oha9u">{review.explanation}</p>
                    <p className="mt-1 text-xs text-ink-muted" data-id="element-172" data-mp="3lstn46er7cji">
                      Entries included ({review.entryIds.length}): {review.entryIds.join(', ')}
                    </p>
                  </li>)}
              </ol>}
          </section>
        </div>
      </div>
    </div>;
}